import { useState } from 'react'

import { CopyIcon } from '@chakra-ui/icons'
import { IconButton, useToast } from '@chakra-ui/react'

interface ClickToCopyButtonIProps {
  textToCopy: string
  ariaLabel?: string
  successMessage?: string
  errorMessage?: string
  toastDuration?: number
}

/**
 * A reusable button component that copies text to the clipboard.
 * Shows visual feedback by changing color and displays toast notifications.
 *
 * @param textToCopy - The text to copy to clipboard
 * @param size - Size of the icon button (defaults to 'sm')
 * @param ariaLabel - Accessibility label for the button (defaults to 'Copy to clipboard')
 * @param successMessage - Custom success toast message (defaults to 'Copied to clipboard')
 * @param errorMessage - Custom error toast message (defaults to 'Failed to copy')
 * @param toastDuration - Duration to show toast in milliseconds (defaults to 2000)
 */
export const ClickToCopyButton = ({
  textToCopy,
  ariaLabel = 'Copy to clipboard',
  successMessage = 'Copied to clipboard',
  errorMessage = 'Failed to copy',
  toastDuration = 2_000,
}: ClickToCopyButtonIProps) => {
  const toast = useToast()
  const [isCopied, setIsCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy)
      setIsCopied(true)
      toast({
        title: successMessage,
        status: 'success',
        duration: toastDuration,
        isClosable: true,
      })

      setTimeout(() => {
        setIsCopied(false)
      }, toastDuration)
    } catch (err) {
      toast({
        title: errorMessage,
        status: 'error',
        duration: toastDuration,
        isClosable: true,
      })
    }
  }

  return (
    <IconButton
      aria-label={ariaLabel}
      colorScheme={isCopied ? 'green' : 'gray'}
      flexShrink={0}
      icon={<CopyIcon />}
      onClick={handleCopy}
      size='sm'
    />
  )
}
